// EditEngineerModal.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Paper,
  Chip,
  Avatar,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import CustomTooltip from "../CustomTooltip";
const EditEngineerModal = ({
  open,
  onClose,
  project,
  onUpdate,
  showSnackbar,
}) => {
  const { token } = useAuth();
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch engineers from API
  const fetchEngineers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        role: "ENGINEER",
        status: "APPROVED",
        limit: 50,
      };

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      const response = await axios.get(`${url}/api/users/fetch`, config);

      if (response.data?.users) {
        setEngineers(response.data.users);
        
        // If project has assigned engineer, set as selected
        if (project.assignedEngineer) {
          const currentEngineer = response.data.users.find(
            eng => eng._id === project.assignedEngineer._id || 
                   eng._id === project.assignedEngineer
          );
          if (currentEngineer) {
            setSelectedEngineer(currentEngineer);
          }
        }
      } else {
        setEngineers([]);
      }
    } catch (err) {
      console.error("Error fetching engineers:", err);
      setError("Failed to load engineers. Please try again.");
      setEngineers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchEngineers();
      setSearchInput("");
      setError(null);
    }
  }, [open]);

  const handleEngineerSelect = (event, newValue) => {
    setSelectedEngineer(newValue);
  };

  // Calculate engineer stats
  const getEngineerStats = (engineer) => {
    const stats = engineer.projectsRecords || {};
    return {
      assigned: stats.projectAssigned || 0,
      completed: stats.projectCompleted || 0,
    };
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Get color for avatar
  const getAvatarColor = (name) => {
    const colors = [
      COLORS.SECONDARY,
      "#8CD47E",
      "#4CAF50",
      "#2196F3",
      "#FF9800",
      "#9C27B0",
    ];
    if (!name) return COLORS.SECONDARY;
    const index = name.length % colors.length;
    return colors[index];
  };

  const handleUpdate = async () => {
    if (!selectedEngineer) {
      showSnackbar("Please select an engineer", "warning");
      return;
    }

    // Check if selecting the same engineer
    if (project.assignedEngineer && selectedEngineer._id === project.assignedEngineer._id) {
      showSnackbar("Same engineer is already assigned", "info");
      onClose();
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const response = await axios.post(
        `${url}/api/inhouse-projects/assign-engineer/${project._id}`,
        {
          engineerId: selectedEngineer._id,
        },
        config
      );

      if (response.status === 200) {
        showSnackbar(
          `Engineer updated to ${selectedEngineer.name} successfully!`,
          "success"
        );
        
        if (onUpdate) {
          onUpdate(response.data.project);
        }
        
        onClose();
      }
    } catch (err) {
      console.error("Error updating engineer:", err);
      const errorMsg = err.response?.data?.message || "Failed to update engineer";
      setError(errorMsg);
      showSnackbar(errorMsg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90vw", sm: 600, md: 700 },
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
          zIndex: 1401,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2, sm: 3 },
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            bgcolor: COLORS.SECONDARY + "08",
          }}
        >
          <Box>
            <Typography
              sx={{
                ...TYPOGRAPHY.H2,
                color: COLORS.SECONDARY,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              Edit Assigned Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                color: COLORS.TEXT,
                mt: 0.5,
              }}
            >
              Project: {project.projectName}
            </Typography>
          </Box>
        
            <IconButton onClick={onClose} size="small">
              <Icon name="close" color={COLORS.ERROR} />
            </IconButton>
         
        </Box>

        {/* Current Assignment */}
        {/* {project.assignedEngineer && (
          <Alert
            severity="info"
            sx={{
              m: { xs: 2, sm: 3 },
              mt: 2,
              borderRadius: 1,
              border: `1px solid ${COLORS.SECONDARY}20`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon name="info" color={COLORS.SECONDARY} />
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL }}>
                Current engineer:{" "}
                <strong style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer.name || project.assignedEngineerName}
                </strong>
              </Typography>
            </Box>
          </Alert>
        )} */}

        {/* Search and Selection */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.SECONDARY,
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Select New Engineer
          </Typography>
          
          <Autocomplete
            value={selectedEngineer}
            onChange={handleEngineerSelect}
            inputValue={searchInput}
            onInputChange={(event, newInputValue) => {
              setSearchInput(newInputValue);
              if (newInputValue.length > 2) {
                setSearchLoading(true);
                // Simulate search
                setTimeout(() => setSearchLoading(false), 500);
              }
            }}
            options={engineers}
            getOptionLabel={(option) => option?.name || ""}
            loading={searchLoading || loading}
            disabled={submitting}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type to search engineers..."
                variant="outlined"
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: COLORS.SECONDARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.SECONDARY,
                      borderWidth: 2,
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Icon
                      name="search"
                      color={COLORS.LIGHT_SECONDARY}
                      sx={{ mr: 1, ml: 0.5 }}
                    />
                  ),
                  endAdornment: (
                    <>
                      {(searchLoading || loading) && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option._id}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 2,
                  width: "100%",
                  py: 1,
                }}>
                  {/* <Avatar
                    sx={{
                      bgcolor: getAvatarColor(option.name),
                      width: 40,
                      height: 40,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {getInitials(option.name)}
                  </Avatar> */}
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: COLORS.TEXT,
                        mb: 0.25,
                      }}
                    >
                      {option.name}
                      {project.assignedEngineer && option._id === project.assignedEngineer._id && (
                        <Chip
                          label="Current"
                          size="small"
                          sx={{
                            ml: 1,
                            bgcolor: COLORS.SECONDARY,
                            color: "#fff",
                            fontSize: "0.625rem",
                            height: 18,
                          }}
                        />
                      )}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {option.email}
                    </Typography>
                  </Box>
                  
                  {/* <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Projects Assigned">
                      <Chip
                        label={`${getEngineerStats(option).assigned}`}
                        size="small"
                        sx={{
                          bgcolor: "#E3F2FD",
                          color: COLORS.SECONDARY,
                          fontSize: "0.75rem",
                          height: 22,
                          minWidth: 40,
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Projects Completed">
                      <Chip
                        label={`${getEngineerStats(option).completed}`}
                        size="small"
                        sx={{
                          bgcolor: "#E8F5E9",
                          color: "#4CAF50",
                          fontSize: "0.75rem",
                          height: 22,
                          minWidth: 40,
                        }}
                      />
                    </Tooltip>
                  </Box> */}
                </Box>
              </Box>
            )}
            noOptionsText={
              searchInput ? "No engineers found" : "No engineers available"
            }
            isOptionEqualToValue={(option, value) => 
              option._id === value?._id
            }
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.name?.toLowerCase().includes(inputValue) ||
                option.email?.toLowerCase().includes(inputValue) ||
                option.phone?.includes(inputValue)
              );
            }}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 9999,
                },
              },
            }}
          />
          
          {selectedEngineer && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: `2px solid ${COLORS.SECONDARY}`,
                bgcolor: COLORS.SECONDARY + "08",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* <Avatar
                  sx={{
                    bgcolor: getAvatarColor(selectedEngineer.name),
                    width: 50,
                    height: 50,
                    fontWeight: 600,
                  }}
                >
                  {getInitials(selectedEngineer.name)}
                </Avatar> */}
                
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.SECONDARY,
                      mb: 0.5,
                    }}
                  >
                    {selectedEngineer.name}
                  </Typography>
                  
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {selectedEngineer.email}
                    </Typography>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {selectedEngineer.phone}
                    </Typography>
                  </Box>
{/*                   
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <Tooltip title="Projects Assigned">
                      <Chip
                        icon={<Icon name="briefcase" size={14} />}
                        label={`${getEngineerStats(selectedEngineer).assigned} Assigned`}
                        size="small"
                        sx={{
                          bgcolor: "#E3F2FD",
                          color: COLORS.SECONDARY,
                          ...TYPOGRAPHY.BODY_SMALL,
                          height: 24,
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Projects Completed">
                      <Chip
                        icon={<Icon name="checkCircle" size={14} />}
                        label={`${getEngineerStats(selectedEngineer).completed} Completed`}
                        size="small"
                        sx={{
                          bgcolor: "#E8F5E9",
                          color: "#4CAF50",
                          ...TYPOGRAPHY.BODY_SMALL,
                          height: 24,
                        }}
                      />
                    </Tooltip>
                  </Box> */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            <Alert
              severity="error"
              sx={{ borderRadius: 1 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: { xs: 2, sm: 3 },
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            gap: 2,
            mt: "auto",
          }}
        >
      
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              startIcon={<Icon name="close2" />}
              sx={{
                borderColor: COLORS.LIGHT_SECONDARY,
                color: COLORS.TEXT,
                "&:hover": {
                  borderColor: COLORS.SECONDARY,
                  color: COLORS.SECONDARY,
                },
              }}
            >
              Cancel
            </Button>
     
          
      
            <span>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={!selectedEngineer || submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon name="check" />
                  )
                }
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  "&:hover": {
                    bgcolor: COLORS.SECONDARY,
                    opacity: 0.9,
                  },
                  "&.Mui-disabled": {
                    bgcolor: COLORS.LIGHT_SECONDARY,
                    color: "#fff",
                  },
                }}
              >
                {submitting ? "Updating..." : "Update Engineer"}
              </Button>
            </span>
        
        </Box>
      </Box>
    </>
  );
};

export default EditEngineerModal;